'use client';

/**
 * Product Wishes Panel — Lists buyer product wishes for a project.
 *
 * Fetches BW_ products linked via FOR_PROJECT from the project_explore
 * data gateway strategy (same data source as explorer sub-folders).
 *
 * @layer presentation
 * @arch_component_type copilot_insight
 * @arch_domain discovery
 */

import { Lightbulb, Loader2, AlertCircle } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { cn } from '@/lib/utils/cn';

interface ProductWish {
  semantic_id: string;
  name: string;
  category: string | null;
  created_by: string | null;
}

interface ProjectExploreResult {
  rfqs: unknown[];
  pos: unknown[];
  mcps: unknown[];
  product_wishes: ProductWish[];
}

export function ProductWishesPanel({ projectId }: { projectId: string | null }) {
  const exploreStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'explore'));
  const { data, isLoading, error } = useStrategyData<ProjectExploreResult>(
    exploreStrategy ?? 'project_explore',
    { semantic_id: projectId! },
    !!projectId,
  );

  if (!projectId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
        <Lightbulb className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">Select a project to see product wishes</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-destructive">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        Failed to load: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  const wishes = data?.data?.product_wishes ?? [];

  if (wishes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
        <Lightbulb className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm font-medium">No product wishes yet</p>
        <p className="text-xs mt-1">Create one from Discovery → Add Product → Custom/Bespoke</p>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-2">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="w-4 h-4 text-orange-400" />
        <span className="text-sm font-semibold">Product Wishes</span>
        <span className="text-xs text-muted-foreground ml-auto">{wishes.length} item{wishes.length !== 1 ? 's' : ''}</span>
      </div>
      {wishes.map((wish) => (
        <div
          key={wish.semantic_id}
          className={cn(
            'flex items-center gap-3 px-3 py-2 rounded-lg',
            'border border-border bg-card hover:bg-accent/50 transition-colors',
          )}
        >
          <Lightbulb className="w-4 h-4 text-orange-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{wish.name}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {wish.category && <span className="capitalize">{wish.category}</span>}
              <span className="font-mono text-[10px] opacity-60">{wish.semantic_id}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
